import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Minus, Plus, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const PLANS = {
  essential: {
    name: "Essential", tag: "For lean studios",
    rates: { super_admin: 2500, manager: 1500, freelancer: 700 },
    perks: [
      "Projects — up to 3",
      "Tasks — with restrictions",
      "Manager task assignment",
      "Freelancer management",
      "Invoice management — up to 5",
    ],
  },
  professional: {
    name: "Professional", tag: "For scaling ops",
    rates: { super_admin: 10000, manager: 4500, freelancer: 700 },
    perks: [
      "All of Essential — no restrictions",
      "Client portal · Unlimited clients",
      "Push notifications",
      "Reports & analytics",
      "Productivity analytics",
      "Freelancer performance",
      "Manager workload & availability",
      "File / Document management",
      "Custom workflow · Audit log",
      "Custom integrations",
      "Priority support · SLA · SAML",
    ],
    featured: true,
  },
};

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector('script[src*="checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      if (window.Razorpay) return resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function Stepper({ label, value, onChange, min = 0 }) {
  return (
    <div className="flex items-center justify-between border border-black/10 rounded-2xl px-4 py-3 bg-white">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs text-ink/60 font-mono-tight">seats</div>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid={`stepper-dec-${label.toLowerCase()}`} type="button" onClick={() => onChange(Math.max(min, value - 1))} className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:bg-cream-2"><Minus size={14} /></button>
        <div data-testid={`stepper-val-${label.toLowerCase()}`} className="w-8 text-center font-mono-tight">{value}</div>
        <button data-testid={`stepper-inc-${label.toLowerCase()}`} type="button" onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:bg-cream-2"><Plus size={14} /></button>
      </div>
    </div>
  );
}

export default function Plans() {
  const [tier, setTier] = useState(null);
  const [qty, setQty] = useState({ super_admin: 1, manager: 1, freelancer: 3 });
  const [busy, setBusy] = useState(false);
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const openTier = (t) => {
    setTier(t);
    setQty({ super_admin: 1, manager: 1, freelancer: 3 });
  };

  const total = tier
    ? qty.super_admin * PLANS[tier].rates.super_admin
    + qty.manager * PLANS[tier].rates.manager
    + qty.freelancer * PLANS[tier].rates.freelancer
    : 0;

  const proceed = async () => {
    nav(user ? "/dashboard" : "/signup");
  };

  // eslint-disable-next-line no-unused-vars
  const legacyProceed = async () => {
    if (!user) {
      toast.info("Please sign in to complete checkout.");
      nav("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/plans/checkout", { tier, quantities: qty });
      if (data.mocked) {
        toast.success("Razorpay keys not configured yet — subscription recorded in demo mode.");
        setTier(null);
        setBusy(false);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        toast.error("Razorpay could not load. If you're using an ad-blocker, disable it for this site and retry.");
        setBusy(false);
        return;
      }
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: data.business_name || "FreLancTrac",
        description: `${PLANS[tier].name} plan — ₹${(data.amount / 100).toLocaleString("en-IN")}/month · monthly autopay`,
        prefill: { email: "", contact: "" },
        theme: { color: "#1B4332" },
        config: {
          display: {
            blocks: {
              upi_block: {
                name: "Pay via UPI",
                instruments: [{ method: "upi" }],
              },
              banks_block: {
                name: "Pay via Bank Account / Card",
                instruments: [{ method: "emandate" }, { method: "card" }],
              },
            },
            sequence: ["block.upi_block", "block.banks_block"],
            preferences: { show_default_blocks: false },
          },
        },
        modal: {
          ondismiss: () => { setBusy(false); toast.message("Checkout closed. You can retry any time."); },
          escape: true, backdropclose: false, confirm_close: true,
        },
        retry: { enabled: true, max_count: 3 },
        handler: async (resp) => {
          try {
            const { data: verifyData } = await api.post("/plans/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refresh();
            toast.success(`Subscription active — ${verifyData.plan_tier === "professional" ? "Professional" : "Essential"} plan unlocked.`);
            setTier(null);
            nav("/billing");
          } catch (err) { toast.error(formatApiError(err)); }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => toast.error(resp?.error?.description || "Payment failed"));
      rzp.open();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream/80 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl leading-none tracking-tight" data-testid="plans-brand">FreLancTrac<span className="text-royal-700">.</span></Link>
          <div className="flex items-center gap-3">
            {user ? <Link to="/dashboard" className="btn-primary">Log In →</Link> : <Link to="/login" className="btn-outline">Log in</Link>}
          </div>
        </div>
      </header>

      <section className="pt-16 pb-8 max-w-5xl mx-auto px-6 text-center">
        <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-none mt-6 text-royal-800">Pick a plan. Pay per seat.</h1>
        <p className="mt-4 text-ink/70 max-w-2xl mx-auto">Two clean tiers. Set the number of SuperAdmins, Managers and Freelancers — the mandate adjusts on the next cycle.</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="plans-grid">
        {Object.entries(PLANS).map(([key, p]) => (
          <div key={key} data-testid={`plan-card-${key}`}
            className={`rounded-[1.75rem] p-8 card-lift border ${p.featured ? "bg-royal-800 text-cream border-royal-800" : "bg-white text-ink border-black/5"}`}>
            <div className={`label-caps ${p.featured ? "text-gold" : ""}`}>{p.tag}</div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className={`font-display text-4xl ${p.featured ? "" : "text-royal-800"}`}>{p.name}</div>
              {p.featured && <div className="text-xs bg-gold text-ink px-2 py-0.5 rounded-full font-semibold">Recommended</div>}
            </div>
            <ul className="mt-6 space-y-2 text-sm">
              {Object.entries(p.rates).map(([role, rate]) => (
                <li key={role} className={`flex justify-between border-b ${p.featured ? "border-white/10" : "border-black/5"} pb-2`}>
                  <span className="opacity-80 capitalize">{role.replace("_", " ")}</span>
                  <span className="font-mono-tight">₹ {rate.toLocaleString("en-IN")}<span className="opacity-60 text-xs">/mo · per seat</span></span>
                </li>
              ))}
            </ul>
            <ul className="mt-5 space-y-2 text-sm">
              {p.perks.map((perk) => (
                <li key={perk} className={`flex items-center gap-2 ${p.featured ? "text-cream/85" : "text-ink/75"}`}>
                  <CheckCircle2 size={14} className={p.featured ? "text-gold" : "text-royal-600"} /> {perk}
                </li>
              ))}
            </ul>
            <button data-testid={`plan-proceed-${key}`} onClick={() => openTier(key)}
              className={`mt-8 inline-flex items-center gap-2 ${p.featured ? "bg-gold text-ink" : "btn-primary"} rounded-full px-5 py-2.5 font-semibold text-sm`}>
              Proceed with {p.name} <ArrowRight size={14} />
            </button>
          </div>
        ))}
      </section>

      <Dialog open={!!tier} onOpenChange={(o) => !o && setTier(null)}>
        <DialogContent className="rounded-[1.5rem] border border-black/10 max-w-md p-0 overflow-hidden" data-testid="plans-checkout-dialog">
          {tier && (
            <>
              <DialogHeader className="bg-royal-800 text-cream px-6 py-5">
                <div className="label-caps text-cream/70">{PLANS[tier].name} · monthly autopay</div>
                <DialogTitle className="font-display text-3xl mt-1">Configure your seats.</DialogTitle>
                <DialogDescription className="text-cream/70 text-sm">Set the number of seats for each role. Total updates instantly; monthly autopay via Razorpay e-mandate.</DialogDescription>
              </DialogHeader>
              <div className="p-6 space-y-3">
                <Stepper label="SuperAdmin" value={qty.super_admin} onChange={(v) => setQty({ ...qty, super_admin: v })} min={1} />
                <Stepper label="Manager" value={qty.manager} onChange={(v) => setQty({ ...qty, manager: v })} min={0} />
                <Stepper label="Freelancer" value={qty.freelancer} onChange={(v) => setQty({ ...qty, freelancer: v })} min={0} />
                <div className="mt-4 border-t border-black/10 pt-4 flex justify-between items-baseline">
                  <div>
                    <div className="label-caps">Monthly total</div>
                    <div className="text-xs text-ink/60">Billed monthly via Razorpay mandate</div>
                  </div>
                  <div className="font-display text-3xl text-royal-800" data-testid="checkout-total">₹ {total.toLocaleString("en-IN")}</div>
                </div>
                <button data-testid="checkout-proceed" disabled={busy || total === 0} onClick={proceed} className="w-full mt-3 btn-primary disabled:opacity-50">
                  {busy ? "Setting up mandate…" : "Proceed to Checkout →"}
                </button>
                <div className="text-xs text-ink/50 text-center">You'll authorise a monthly e-mandate. Cancel anytime from the Billing page.</div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
